import type { Turn } from "../api/types";
import type { ConversationState, LogEntry } from "./types";

export const initialState: ConversationState = {
  sessionId: null,
  log: [],
  status: "starting",
  error: null,
  collectedData: {},
};

export type Action =
  | { type: "START" }
  | { type: "SESSION_STARTED"; turn: Turn }
  | { type: "USER_SENT"; id: string; text: string }
  | { type: "BOT_REPLY"; turn: Turn }
  | { type: "ERROR"; message: string }
  | { type: "RESTORE"; sessionId: string; log: LogEntry[] }
  // The answers, re-fetched from the server after a refresh. They are never
  // cached in the browser (PII at rest), so RESTORE alone leaves them empty.
  | { type: "COLLECTED_RESTORED"; turn: Turn }
  | { type: "RETRANSLATED"; turn: Turn }
  | { type: "RESET" };

let seq = 0;
/** Monotonic id for log entries (stable React keys; order-preserving). */
export function nextId(): string {
  seq += 1;
  return `e${seq}`;
}

function mergeCollected(
  prev: Record<string, unknown>,
  turn: Turn,
): Record<string, unknown> {
  return turn.collected_data ? { ...prev, ...turn.collected_data } : prev;
}

export function reducer(state: ConversationState, action: Action): ConversationState {
  switch (action.type) {
    case "START":
      return { ...initialState, status: "starting" };

    case "SESSION_STARTED":
      return {
        ...state,
        sessionId: action.turn.session_id,
        log: [{ id: nextId(), role: "bot", turn: action.turn }],
        status: "active",
        error: null,
        collectedData: mergeCollected(state.collectedData, action.turn),
      };

    case "USER_SENT":
      return {
        ...state,
        log: [...state.log, { id: action.id, role: "user", text: action.text }],
        status: "sending",
        error: null,
      };

    case "BOT_REPLY":
      return {
        ...state,
        sessionId: action.turn.session_id || state.sessionId,
        log: [...state.log, { id: nextId(), role: "bot", turn: action.turn }],
        status: "active",
        error: null,
        collectedData: mergeCollected(state.collectedData, action.turn),
      };

    case "ERROR":
      return { ...state, status: "error", error: action.message };

    case "RESTORE":
      return {
        ...state,
        sessionId: action.sessionId,
        log: action.log,
        status: "active",
        error: null,
      };

    // Answers fetched back from the server on resume. Only collectedData is
    // touched: the restored transcript is already on screen and must not be
    // disturbed (re-rendering it would replay the typing animations).
    case "COLLECTED_RESTORED":
      return {
        ...state,
        collectedData: mergeCollected(state.collectedData, action.turn),
      };

    // The current question re-rendered in a newly chosen language. Only the
    // LAST bot turn is replaced: earlier turns stay exactly as the resident was
    // asked them, so the transcript remains an honest record (forward-only).
    case "RETRANSLATED": {
      let at = -1;
      for (let i = state.log.length - 1; i >= 0; i--) {
        if (state.log[i].role === "bot") {
          at = i;
          break;
        }
      }
      const entry = at === -1 ? null : state.log[at];
      // Narrowed explicitly: only a bot entry carries a turn to replace.
      if (!entry || entry.role !== "bot") return state;
      const log = [...state.log];
      log[at] = { ...entry, turn: action.turn };
      return { ...state, log, error: null };
    }

    case "RESET":
      return { ...initialState };

    default:
      return state;
  }
}

/** The current interactive bot turn (last bot entry), if any. */
export function currentTurn(state: ConversationState): Turn | null {
  for (let i = state.log.length - 1; i >= 0; i--) {
    const e = state.log[i];
    if (e.role === "bot") return e.turn;
  }
  return null;
}
